import { AdjustmentFrequency, ManualAdjustment } from '@amzn/awscat-portfolio-assessment-types';

import { DAYS_PER_YEAR, HOURS_PER_DAY, MONTHS_PER_YEAR } from '../../../common/uomConversion';
import { awsServicesMap } from './constants';
import { AdjustmentItem, AdjustmentItemSelected, AdjustmentType } from './interfaces';

/**
 * For every adjustment in the db, split apart the aws and on-prem pieces into their own objects to be displayed separately
 * @param adjustments 
 * @returns 
 */
export const convertDBAdjustmentToDisplay = (adjustments: ManualAdjustment[]): AdjustmentItem[] => {
    const adjustmentItems: AdjustmentItem[] = [];
    adjustments.map(adjustment => {
        if (adjustment.aws) {
            adjustmentItems.push({
                portfolioId: adjustment.portfolioId, 
                tcoAssessmentId: adjustment.tcoAssessmentId,
                adjustmentId: adjustment.adjustmentId,
                createdAt: adjustment.createdAt,
                createdBy: adjustment.createdBy,
                type: AdjustmentType.AWS,
                ...adjustment.aws
            });
        }
        if (adjustment.onPremises) {
            adjustmentItems.push({
                portfolioId: adjustment.portfolioId, 
                tcoAssessmentId: adjustment.tcoAssessmentId,
                adjustmentId: adjustment.adjustmentId,
                createdAt: adjustment.createdAt,
                createdBy: adjustment.createdBy,
                type: AdjustmentType.ON_PREMISES,
                ...adjustment.onPremises
            });
        }
    });
    return adjustmentItems;
};

export const calculateYearlyCost = (amount: number, frequency: AdjustmentFrequency, tcoDuration: number): number => {
    switch (frequency) {
        case AdjustmentFrequency.HOURLY:
            return amount * HOURS_PER_DAY * DAYS_PER_YEAR;
        case AdjustmentFrequency.MONTHLY:
            return amount * MONTHS_PER_YEAR;
        case AdjustmentFrequency.TOTAL:
            return tcoDuration === 0 ? amount : amount / tcoDuration;
        case AdjustmentFrequency.YEARLY:
        default:
            return amount;
    }
};

export const calculateTotalCost = (amount: number, frequency: AdjustmentFrequency, tcoDuration: number): number => {
    switch (frequency) {
        case AdjustmentFrequency.HOURLY:
            return amount * HOURS_PER_DAY * DAYS_PER_YEAR * tcoDuration;
        case AdjustmentFrequency.MONTHLY:
            return amount * MONTHS_PER_YEAR * tcoDuration;
        case AdjustmentFrequency.YEARLY:
            return amount * tcoDuration;
        case AdjustmentFrequency.TOTAL:
        default:
            return amount;
    }
};

/**
 * For AWS adjustments, the user can select a service as the title
 * the id for that service is stored in the db, but the service name should be
 * displayed to the user
 * @param item 
 * @returns 
 */
export const getTitleToDisplay = (item: AdjustmentItem): string => {
    return item.type === AdjustmentType.AWS ? (awsServicesMap.get(item.title)?.name ?? item.title) : item.title;
};

/**
 * Returns a manual adjustment with either the aws or onPremises property set based on the given adjustment item
 * The aws or onPremises property that is not given by the adjustment item is set to undefined (removed)
 * @param item 
 * @returns 
 */
export const getManualAdjustmentWithAwsOrOnPremRemoved = (item: AdjustmentItem): ManualAdjustment => {
    const adjustment: ManualAdjustment = {
        portfolioId: item.portfolioId,
        tcoAssessmentId: item.tcoAssessmentId,
        adjustmentId: item.adjustmentId,
        createdAt: item.createdAt,
        createdBy: item.createdBy,
    };

    if (item.type === AdjustmentType.AWS) {
        adjustment.aws = {
            title: item.title,
            description: item.description,
            category: item.category,
            amount: item.amount,
            frequency: item.frequency,
            inScope: item.inScope,
            awsSupport: item.awsSupport,
        };
        adjustment.onPremises = undefined;
    } else {
        adjustment.onPremises = {
            title: item.title,
            description: item.description,
            category: item.category,
            amount: item.amount,
            frequency: item.frequency,
            inScope: item.inScope,
        };
        adjustment.aws = undefined;
    }

    return adjustment;
};

/**
 * Returns a map of all selected adjustment id to an array of AdjustmentItems with that adjustment id 
 * and sets whether the item was selected by the user
 * Used when showing the delete modal
 * @param selectedAdjustments 
 * @param allAdjustments 
 * @returns 
 */
export const getAdjustmentMap = (selectedAdjustments: AdjustmentItem[], allAdjustments?: AdjustmentItem[]): Map<string, AdjustmentItemSelected[]> => {
    const map: Map<string, AdjustmentItemSelected[]> = new Map<string, AdjustmentItemSelected[]>();

    selectedAdjustments.forEach(item => {
        let adjustmentArray = map.get(item.adjustmentId);
        if (adjustmentArray) {
            adjustmentArray.push({...item, selected: true});
        } else {
            adjustmentArray = [{...item, selected: true}];
            if (allAdjustments) {
                const corresponding = getUnSelectedCorresponding(item, allAdjustments, selectedAdjustments);
                if (corresponding) {
                    adjustmentArray.push({...corresponding, selected: false});
                }
            }
        }
        map.set(item.adjustmentId, adjustmentArray);
    });
    return map;
};

/**
 * Returns a corresponding adjustment that has not been selected by the user, or null if one does not exist
 * @param adjustment 
 * @param selectedAdjustments 
 * @param allAdjustments 
 * @returns 
 */
export const getUnSelectedCorresponding = (
    adjustment: AdjustmentItem, allAdjustments: AdjustmentItem[], selectedAdjustments?: AdjustmentItem[]
): AdjustmentItem | null => {
    if (selectedAdjustments) {
        const selectedCorresponding = 
            selectedAdjustments.find(selected => selected.adjustmentId === adjustment.adjustmentId && selected.type !== adjustment.type);
        if (selectedCorresponding) {
            return null;
        }
    }
    const unSelectedCorresponding = 
        allAdjustments.find(allAdjustment => allAdjustment.adjustmentId === adjustment.adjustmentId && allAdjustment.type !== adjustment.type);
    return unSelectedCorresponding ?? null;
};

/**
 * Returns true if there are any selected adjustments with a corresponding adjustment that has not been selected
 * @param selectedItems 
 * @param allItems 
 * @returns 
 */
export const showDeleteCorrespondingOption = (selectedItems: AdjustmentItem[], allItems: AdjustmentItem[]): boolean => {
    const hasNonSelectedCorrespondingAdjustment = selectedItems.some(selectedItem => {
        return getUnSelectedCorresponding(selectedItem, allItems, selectedItems) ? true : false;
    });
    return hasNonSelectedCorrespondingAdjustment;
};
